import {
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";
import {
  Alert,
  AlertActionCloseButton,
  AlertGroup,
  AlertVariant,
  Modal,
  ModalVariant,
} from "@patternfly/react-core";
import { useTranslation } from "react-i18next";

type AlertItem = {
  id: number;
  message: string;
  variant: AlertVariant;
};

type ContentAlertContextType = {
  postAlert: (variant: AlertVariant, message: string) => void;
};

const ContentAlertContext = createContext<ContentAlertContextType | null>(null);

export const useContentAlert = () => {
  const ctx = useContext(ContentAlertContext);
  if (!ctx) throw new Error("useContentAlert must be used inside provider");
  return ctx;
};

export const ContentAlertProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const postAlert = (variant: AlertVariant, message: string) => {
    const id = Date.now();
    const text = t(message);

    if (variant === AlertVariant.success) {
      setAlerts([]);
      setShowSuccessModal(true);
    } else {
      setAlerts((prev) => [...prev, { id, message: text, variant }]);
    }
  };

  const removeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <ContentAlertContext.Provider value={{ postAlert }}>
      <AlertGroup isToast>
        {alerts.map(({ id, variant, message }) => (
          <Alert
            key={id}
            variant={variant}
            title={message}
            actionClose={
              <AlertActionCloseButton onClose={() => removeAlert(id)} />
            }
          />
        ))}
      </AlertGroup>

      <Modal
        variant={ModalVariant.small}
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        header={<h1>{t("contentAlert.modal-title")}</h1>}
        aria-label={t("contentAlert.modal-title")}
      >
        <h1>{t("contentAlert.modal-message")}</h1>
        <h1>{t("contentAlert.modal-submessage")}</h1>
        <div className="nc-modal-button">
          <button
            className="pf-c-button pf-m-primary"
            onClick={() => (location.href = "./")}
          >
            {t("contentAlert.modal-button")}
          </button>
        </div>
      </Modal>

      {children}
    </ContentAlertContext.Provider>
  );
};